/**
 * ChannelCalendarCard — per-channel calendar control (Phase 2.3.5 C).
 *
 * Shown inline for each channel on the Channels page. Handles:
 * - mode (preview / live / unconfigured) with clear badge
 * - connection status + email
 * - Google Connect button (disabled when server not configured)
 * - calendar picker (when connected OR in preview)
 * - booking mode selector
 * - slot duration, buffer, timezone
 * - preview toggle
 * - disconnect, test
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import TimezoneCombobox from './TimezoneCombobox';
import { Calendar as CalIcon, CheckCircle, XCircle, Loader2, Link2, RefreshCw, ShieldAlert, Zap, Eye, ChevronDown, ChevronUp, FlaskConical } from 'lucide-react';
import { API, useAuth } from '../App';
import { useDemoMode } from '../contexts/DemoModeContext';

const BOOKING_MODE_OPTIONS = [
  {
    value: 'fully_automated',
    label: 'AI + Auto Confirm',
    description: 'System checks availability and auto-creates the booking on the calendar.',
  },
  {
    value: 'ai_with_approval',
    label: 'AI + Human Approval (default)',
    description: 'AI collects details. A human confirms before the calendar is written.',
  },
  {
    value: 'human_assisted',
    label: 'AI + Escalate Only',
    description: 'No booking logic. Every request is handed off to a human.',
  },
];

const ChannelCalendarCard = ({ channel }) => {
  const { token, user } = useAuth();
  const demo = useDemoMode();
  const isOwner = user?.role === 'owner' || user?.is_super_admin;
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    calendar_id: '', timezone: '', slot_duration_minutes: 30, buffer_minutes: 0,
    booking_mode: 'ai_with_approval', preview_enabled: false,
  });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/channels/${channel.id}/calendar/status`, { headers: authHeaders });
      setStatus(res.data);
      setForm(f => ({
        ...f,
        calendar_id: res.data.calendar_id || f.calendar_id,
        timezone: res.data.timezone || f.timezone,
        slot_duration_minutes: res.data.slot_duration_minutes ?? f.slot_duration_minutes,
        buffer_minutes: res.data.buffer_minutes ?? f.buffer_minutes,
        booking_mode: res.data.booking_mode || f.booking_mode,
        preview_enabled: !!res.data.preview_enabled,
      }));
    } catch (err) {
      console.error('[CAL_CARD]', err);
    }
    // eslint-disable-next-line
  }, [channel.id, token]);

  const fetchCalendars = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/channels/${channel.id}/calendar/list-calendars`, { headers: authHeaders });
      setCalendars(res.data?.calendars || []);
    } catch (err) { /* ignore */ }
    // eslint-disable-next-line
  }, [channel.id, token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { if (status?.connected || status?.preview_active) fetchCalendars(); }, [status?.connected, status?.preview_active, fetchCalendars]);

  const handleConnect = async () => {
    try {
      const res = await axios.post(`${API}/channels/${channel.id}/calendar/oauth/start`, {}, { headers: authHeaders });
      if (res.data?.oauth_url) window.location.href = res.data.oauth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OAuth start failed');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Disconnect Google Calendar from channel "${channel.name}"?`)) return;
    try {
      await axios.post(`${API}/channels/${channel.id}/calendar/disconnect`, {}, { headers: authHeaders });
      toast.success('Disconnected');
      fetchStatus();
    } catch { toast.error('Disconnect failed'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/channels/${channel.id}/calendar/select`, {
        calendar_id: form.calendar_id || 'primary',
        timezone: form.timezone || undefined,
        slot_duration_minutes: Number(form.slot_duration_minutes),
        buffer_minutes: Number(form.buffer_minutes),
        booking_mode: form.booking_mode,
        preview_enabled: form.preview_enabled,
      }, { headers: authHeaders });
      toast.success('Calendar settings saved');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await axios.post(`${API}/channels/${channel.id}/calendar/test`, {}, { headers: authHeaders });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ error: err.response?.data?.detail || 'Test failed' });
    } finally { setTesting(false); }
  };

  if (!status) {
    return (
      <div className="py-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading calendar status for {channel.name}…
      </div>
    );
  }

  const mode = status.mode; // preview | live | unconfigured
  const isDemoChannel = !!channel?.is_demo || (demo.active && status.preview_enabled);
  const modeBadge = mode === 'live'
    ? <Badge className="bg-emerald-600" data-testid={`ch-cal-live-${channel.id}`}><Zap className="h-3 w-3 mr-1" /> Live</Badge>
    : isDemoChannel
      ? <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`ch-cal-demo-${channel.id}`}><FlaskConical className="h-3 w-3 mr-1" /> Demo Calendar</Badge>
      : mode === 'preview'
        ? <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`ch-cal-preview-${channel.id}`}><Eye className="h-3 w-3 mr-1" /> Preview</Badge>
        : <Badge variant="outline" data-testid={`ch-cal-unconfigured-${channel.id}`}>Not connected</Badge>;

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/20" data-testid={`ch-cal-card-${channel.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CalIcon className="h-4 w-4 text-blue-600" />
          Calendar
          {modeBadge}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setExpanded(e => !e)} data-testid={`ch-cal-expand-${channel.id}`}>
          {expanded ? <><ChevronUp className="h-4 w-4 mr-1" /> Hide</> : <><ChevronDown className="h-4 w-4 mr-1" /> Configure</>}
        </Button>
      </div>

      {/* Compact summary line */}
      <div className="text-xs text-muted-foreground">
        {status.connected && !isDemoChannel ? (
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-600" /> Connected as {status.connected_email}</span>
        ) : isDemoChannel ? (
          <span className="flex items-center gap-1"><FlaskConical className="h-3 w-3 text-amber-600" /> Using demo data — no live calendar will be touched</span>
        ) : status.preview_active ? (
          <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-amber-600" /> Preview mode — no live calendar will be touched</span>
        ) : (
          <span className="flex items-center gap-1"><XCircle className="h-3 w-3" /> No calendar configured</span>
        )}
      </div>

      {/* Booking Mode — always visible, primary control */}
      <div className="rounded-md border p-3 bg-background" data-testid={`ch-booking-mode-block-${channel.id}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Label className="text-xs font-semibold">Booking Mode</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5 max-w-md">
              Controls what happens when the AI has collected all booking details.
            </p>
          </div>
          <Select
            value={form.booking_mode}
            onValueChange={async (v) => {
              setForm({ ...form, booking_mode: v });
              // persist immediately for quick UX
              if (!isOwner) return;
              try {
                await axios.post(`${API}/channels/${channel.id}/calendar/select`, {
                  booking_mode: v,
                }, { headers: authHeaders });
                toast.success('Booking Mode updated');
                fetchStatus();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to update Booking Mode');
              }
            }}
            disabled={!isOwner}
          >
            <SelectTrigger className="w-full sm:w-[260px]" data-testid={`ch-cal-mode-${channel.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BOOKING_MODE_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{o.label}</span>
                    <span className="text-[10px] text-muted-foreground">{o.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-[11px] mt-2 text-muted-foreground">
          Current: <span className="font-medium text-foreground">
            {BOOKING_MODE_OPTIONS.find(o => o.value === status.booking_mode)?.label || status.booking_mode}
          </span>
        </div>
      </div>

      {expanded && (
        <>
          {!status.google_configured_on_server && (
            <Alert data-testid={`ch-cal-no-google-${channel.id}`}>
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Google Calendar not yet enabled on this server. Use Preview mode below to test booking flows safely.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Calendar</Label>
              {(status.connected || status.preview_active) ? (
                <Select value={form.calendar_id || 'primary'} onValueChange={v => setForm({...form, calendar_id: v})} disabled={!isOwner}>
                  <SelectTrigger data-testid={`ch-cal-id-${channel.id}`}><SelectValue placeholder="Select calendar..." /></SelectTrigger>
                  <SelectContent>
                    {calendars.length === 0 && <SelectItem value="primary">primary</SelectItem>}
                    {calendars.map(c => <SelectItem key={c.id} value={c.id}>{c.summary}{c.primary ? ' (primary)':''}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value="primary" disabled />
              )}
            </div>
            <div>
              <Label className="text-xs">Timezone (IANA)</Label>
              <TimezoneCombobox
                value={form.timezone}
                onChange={(v) => setForm({ ...form, timezone: v })}
                disabled={!isOwner}
                placeholder="Search — e.g. Asia/Kuala_Lumpur"
                testId={`ch-cal-tz-${channel.id}`}
              />
            </div>
            <div>
              <Label className="text-xs">Slot duration (min)</Label>
              <Input type="number" min={5} step={5} value={form.slot_duration_minutes} onChange={e => setForm({...form, slot_duration_minutes: e.target.value})} disabled={!isOwner} data-testid={`ch-cal-slot-${channel.id}`} />
            </div>
            <div>
              <Label className="text-xs">Buffer (min)</Label>
              <Input type="number" min={0} step={5} value={form.buffer_minutes} onChange={e => setForm({...form, buffer_minutes: e.target.value})} disabled={!isOwner} data-testid={`ch-cal-buffer-${channel.id}`} />
            </div>
            <div className="md:col-span-2 flex items-center justify-between rounded-md border p-2">
              <div>
                <div className="text-sm font-medium flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-amber-600" /> Preview Mode</div>
                <div className="text-[11px] text-muted-foreground">All reads/writes routed to safe simulated provider.</div>
              </div>
              <Switch checked={form.preview_enabled} onCheckedChange={v => setForm({...form, preview_enabled: v})} disabled={!isOwner} data-testid={`ch-cal-preview-toggle-${channel.id}`} />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
            {!status.connected && status.google_configured_on_server && isOwner && (
              <Button onClick={handleConnect} size="sm" data-testid={`ch-cal-connect-${channel.id}`}>
                <Link2 className="h-4 w-4 mr-1" /> Connect Google
              </Button>
            )}
            {status.connected && isOwner && (
              <Button variant="outline" size="sm" onClick={handleDisconnect} data-testid={`ch-cal-disconnect-${channel.id}`}>Disconnect</Button>
            )}
            {isOwner && (
              <>
                <Button onClick={handleSave} size="sm" disabled={saving} data-testid={`ch-cal-save-${channel.id}`}>
                  {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
                </Button>
                <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid={`ch-cal-test-${channel.id}`}>
                  {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Test
                </Button>
              </>
            )}
          </div>

          {testResult && (
            <pre className="text-[10px] bg-muted/60 p-2 rounded overflow-x-auto max-h-40" data-testid={`ch-cal-test-result-${channel.id}`}>
{JSON.stringify(testResult, null, 2)}
            </pre>
          )}
        </>
      )}
    </div>
  );
};

export default ChannelCalendarCard;
